import { AiAccountLimits, AiAgentCatalogItem, AiProfile, AiTargetType } from '../../api';
import * as yup from 'yup';
import { findAgent, isAgentReady } from './ai.derive';

export type AiFieldErrors = Record<string, string>;

export interface IProfileValidationContext {
    /** IDs of every other profile in the draft; the profile under edit is not in this list. */
    otherIds: string[];
    /** Undefined until the catalog has loaded: an unknown agent is not reported as missing or unavailable. */
    agents?: AiAgentCatalogItem[];
}

const ID_PATTERN = /^[a-z][a-z0-9_-]*$/;

const isBlank = (v: unknown): boolean => v === '' || v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));

/** A whole number within bounds. The inputs are typed as numbers but hold '' while empty. */
const isInt = (v: unknown, lo: number, hi: number): boolean =>
    !isBlank(v) && Number.isInteger(Number(v)) && Number(v) >= lo && Number(v) <= hi;

const first = (err: yup.ValidationError): AiFieldErrors => {
    const out: AiFieldErrors = {};
    for (const e of err.inner.length ? err.inner : [err]) {
        const key = e.path || '_';
        if (!out[key]) {
            out[key] = e.message;
        }
    }
    return out;
};

/**
 * Cross-field rules from the PUT /support/ai/configuration description. The server re-checks all of them;
 * this only saves the operator a round trip, and the server's answer stays authoritative.
 */
export function profileSchema(ctx: IProfileValidationContext): yup.Schema<unknown> {
    return yup
        .mixed<AiProfile>()
        .test('id', function (p) {
            if (!p?.id) return this.createError({ path: 'id', message: 'Required.' });
            if (!ID_PATTERN.test(p.id) || p.id.length > 64) {
                return this.createError({ path: 'id', message: 'Lowercase letter first; a–z, 0–9, _ or -; max 64.' });
            }
            if (ctx.otherIds.includes(p.id)) return this.createError({ path: 'id', message: 'Profile ID must be unique.' });
            return true;
        })
        .test('displayName', function (p) {
            return p?.displayName ? true : this.createError({ path: 'displayName', message: 'Required.' });
        })
        .test('target', function (p) {
            if (!p) return true;
            const errors: yup.ValidationError[] = [];
            if (p.targetType === AiTargetType.MODEL) {
                if (!p.models?.length) {
                    errors.push(
                        this.createError({
                            path: 'models',
                            message: 'Select at least one model. An empty list is never unrestricted.',
                        }),
                    );
                }
                if (!p.defaultModel || !(p.models ?? []).includes(p.defaultModel)) {
                    errors.push(
                        this.createError({ path: 'defaultModel', message: 'Default model must be one of the selected models.' }),
                    );
                }
                if (!p.operations?.length) {
                    errors.push(this.createError({ path: 'operations', message: 'Select at least one operation.' }));
                }
            } else if (!p.agentId) {
                errors.push(this.createError({ path: 'agentId', message: 'Select a registered agent.' }));
            } else if (ctx.agents) {
                const agent = findAgent(ctx.agents, p.agentId);
                if (!agent) {
                    errors.push(this.createError({ path: 'agentId', message: 'Select a registered agent.' }));
                } else if (p.enabled && !isAgentReady(agent)) {
                    errors.push(
                        this.createError({
                            path: 'agentId',
                            message: `${agent.name} is UNAVAILABLE. Disable this profile or choose a READY agent (409 AI_AGENT_UNAVAILABLE).`,
                        }),
                    );
                }
            }
            return errors.length ? new yup.ValidationError(errors) : true;
        })
        .test('lifetime', function (p) {
            if (!p) return true;
            if (!isInt(p.defaultLifetimeSeconds, 60, 3600) || !isInt(p.maxLifetimeSeconds, 60, 3600)) {
                return this.createError({ path: 'lifetime', message: 'Lifetimes must be whole seconds between 60 and 3600.' });
            }
            if (Number(p.defaultLifetimeSeconds) > Number(p.maxLifetimeSeconds)) {
                return this.createError({ path: 'lifetime', message: 'Default must not exceed maximum.' });
            }
            return true;
        })
        .test('keyLimits', function (p) {
            if (!p) return true;
            const kl = (p.keyLimits ?? {}) as Partial<Record<'maxBudgetUsd' | 'requestsPerMinute' | 'tokensPerMinute', unknown>>;
            if (!(Number(kl.maxBudgetUsd) > 0)) {
                return this.createError({ path: 'keyLimits', message: 'Key budget must be greater than 0.' });
            }
            const badRpm = !isBlank(kl.requestsPerMinute) && !isInt(kl.requestsPerMinute, 1, 1e9);
            const badTpm = !isBlank(kl.tokensPerMinute) && !isInt(kl.tokensPerMinute, 1, 1e12);
            if (badRpm || badTpm) {
                return this.createError({
                    path: 'keyLimits',
                    message: 'Per-key rate limits must be whole numbers ≥ 1, or blank.',
                });
            }
            return true;
        });
}

export function validateProfile(profile: AiProfile, ctx: IProfileValidationContext): AiFieldErrors {
    try {
        profileSchema(ctx).validateSync(profile, { strict: true, abortEarly: false });
        return {};
    } catch (err) {
        if (err instanceof yup.ValidationError) {
            return first(err);
        }
        throw err;
    }
}

export interface IConfigurationErrors {
    limits: AiFieldErrors;
    /** One entry per profile, in order; an empty object means that profile is valid. */
    profiles: AiFieldErrors[];
    /** Number of invalid fields groups: the count the header shows next to "Unsaved". */
    count: number;
}

export function validateConfiguration(
    config: { accountLimits: AiAccountLimits; profiles: AiProfile[] },
    agents?: AiAgentCatalogItem[],
): IConfigurationErrors {
    const limits = validateLimits(config.accountLimits);
    const profiles = config.profiles.map((p, i) =>
        validateProfile(p, { otherIds: config.profiles.filter((_, j) => j !== i).map(o => o.id), agents }),
    );
    const count = Object.keys(limits).length + profiles.filter(e => Object.keys(e).length > 0).length;
    return { limits, profiles, count };
}

export const accountLimitsSchema = yup
    .mixed<AiAccountLimits>()
    .test('maxBudgetUsd', function (l) {
        return Number(l?.maxBudgetUsd) > 0
            ? true
            : this.createError({ path: 'maxBudgetUsd', message: 'Must be greater than 0. Use issuance off to disable.' });
    })
    .test('requestsPerMinute', function (l) {
        return isInt(l?.requestsPerMinute, 1, 1e9)
            ? true
            : this.createError({ path: 'requestsPerMinute', message: 'Whole number ≥ 1.' });
    })
    .test('tokensPerMinute', function (l) {
        return isInt(l?.tokensPerMinute, 1, 1e12)
            ? true
            : this.createError({ path: 'tokensPerMinute', message: 'Whole number ≥ 1.' });
    });

export function validateLimits(limits: AiAccountLimits): AiFieldErrors {
    try {
        accountLimitsSchema.validateSync(limits, { strict: true, abortEarly: false });
        return {};
    } catch (err) {
        if (err instanceof yup.ValidationError) {
            return first(err);
        }
        throw err;
    }
}
